import { token } from '@styled/tokens';
import { Node as CanvasNode, XYPosition } from '@xyflow/react';
import { ContentState } from 'draft-js';
import { isError } from 'es-toolkit/predicate';
import htmlToDraft from 'html-to-draftjs';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { showErrorToast } from 'components/Toast/Toast';
import { SCROLL_ELEMENT_VISIBILITY } from 'state_management/constants';
import { CRITICAL_PATH_LEVELS } from 'state_management/criticalPath/constants';
import { NODE_TYPES } from 'state_management/NODE_TYPES';
import { ScheduleElementType } from 'state_management/scheduler/ScheduleElementType';
import { ScheduleElement, TimespanResourceElement } from 'state_management/scheduler/scheduler_types';
import { CUSTOM_AMPLIFY_ERROR_ID, PROJECT_NAVIGATION_CANVAS, PROJECT_NAVIGATION_PATH, PROJECT_TABS, SIDEBAR_WIDTH, TOP_HEADER_HEIGHT } from 'state_management/threadedConstants';

import { getFirstValidElementType } from './getFirstValidElementType';
import { DURATION_MAX, DURATION_MIN } from './inputConstraints';
import { ERR_SOMETHINGWENTWRONG } from './messages';
import { GET_CURRENT_PROJECT_ID } from './sessionHelpers';

/**
 * Converts an unknown value to an Error object, ensuring type safety for anything consuming caught errors.
 */
export const unknownToError = (error: unknown, backupMessage: string): Error => {
  if (isError(error)) return error;

  return new Error(typeof error === 'string' ? error : backupMessage);
};

export const prevent = (event: any) => {
  if (event) {
    if (event.preventDefault) {
      event.preventDefault();
    }

    if (event.stopPropagation) {
      event.stopPropagation();
    }

    if (event.stopImmediatePropagation) {
      event.stopImmediatePropagation();
    }
  }

  return false;
};

export function usePrevious<T>(value: T) {
  const ref = React.useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

export function makeAvatarText(name: string): string {
  return (name || '').charAt(0).toUpperCase();
}

export const isValidText = (value: string | number) => {
  const isString = typeof value === 'string';
  const isNumber = typeof value === 'number';

  return !!((isString && value.trim().length) || isNumber);
};

export const extractNumberFromString = (inputString: string): number | null => {
  try {
    const pattern = /\d+/g;
    const matches = inputString?.match(pattern);
    if (matches && matches.length > 0) {
      return parseInt(matches[0], 10);
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

export const removeControlCharactersAndEmojisFromString = (inputString: string) => {
  try {
    // Regular expression to match control characters EXCEPT newlines (\x0A), carriage returns (\x0D), and tabs (\x09)
    // eslint-disable-next-line no-control-regex
    const controlCharactersRegex = /[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F-\x9F]/g;
    const emojiRegex = /[\uD800-\uDFFF\u2600-\u26FF\u2700-\u27BF\u{1F300}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{1F004}-\u{1F0CF}\u{1F170}-\u{1F251}]/gu;
    return inputString?.replaceAll(controlCharactersRegex, '')?.replaceAll(emojiRegex, '');
  } catch (error) {
    return inputString;
  }
};

export const getElementsDataFromClipboard = (clipboardTextData: string, elementType: ScheduleElementType, pasteAsSingleElement: boolean, pasteAsTextBox: boolean) => {
  if (!clipboardTextData) return [];
  if (pasteAsTextBox) {
    return [
      {
        name: clipboardTextData,
        duration: 0,
        type: NODE_TYPES.TEXT_BOX
      }
    ];
  }
  if (pasteAsSingleElement) {
    return [
      {
        name: clipboardTextData,
        duration: 0,
        type: elementType
      }
    ];
  }
  try {
    const parsed = JSON.parse(clipboardTextData);
    const isJSONData = typeof parsed === 'object' && parsed !== null;
    if (isJSONData) return [];
  } catch (error) {
    //
  }
  const activitiesToPaste: { name: string; duration: number; type: ScheduleElementType }[] = [];
  const activityRows = clipboardTextData?.replaceAll('\r', '')?.split('\n');
  if (!activityRows?.length) return [];
  activityRows.forEach((activityRow) => {
    if (activityRow) {
      const activityData = activityRow.split('\t');
      const activityName = removeControlCharactersAndEmojisFromString(activityData?.[0]?.trim());
      const activityDuration = extractNumberFromString(activityData?.[1]);
      if (activityName && typeof activityName === 'string') {
        const newActivity = {
          name: activityName,
          type: elementType,
          duration: activityDuration && !isNaN(activityDuration) && Number.isInteger(activityDuration) && activityDuration >= DURATION_MIN && activityDuration <= DURATION_MAX ? activityDuration : 0
        };
        activitiesToPaste.push(newActivity);
      }
    }
  });
  return activitiesToPaste;
};

export const getElementCountFromClipboardText = (clipboardTextData: string) => {
  if (!clipboardTextData) return 0;
  const activityRows = clipboardTextData?.replaceAll('\r', '')?.split('\n');
  if (!activityRows?.length) return 0;
  let count = 0;
  activityRows.forEach((activityRow) => {
    if (activityRow) {
      const activityData = activityRow.split('\t');
      const activityName = removeControlCharactersAndEmojisFromString(activityData?.[0]?.trim());
      if (activityName && typeof activityName === 'string') {
        count++;
      }
    }
  });
  return count;
};

export const showToastErrorFromApiErrorResponse = (error: any) => {
  const getErrorMessage = (error: any) => {
    return error && error.response && error.response.data && error.response.data.message ? error.response.data.message : '';
  };
  const message = getErrorMessage(error);
  if (message) {
    showErrorToast(message);
  }
};

export const somethingWentWrongError = () => {
  showErrorToast(ERR_SOMETHINGWENTWRONG);
};

export enum OSType {
  WINDOWS,
  MACOS,
  IOS,
  ANDROID,
  LINUX
}

export const getOS = () => {
  const userAgent = window.navigator.userAgent,
    platform = window.navigator.platform,
    macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
    windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
    iosPlatforms = ['iPhone', 'iPad', 'iPod'];

  if (macosPlatforms.indexOf(platform) !== -1) {
    return OSType.MACOS;
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    return OSType.IOS;
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    return OSType.WINDOWS;
  } else if (/Android/.test(userAgent)) {
    return OSType.ANDROID;
  } else if (/Linux/.test(platform)) {
    return OSType.LINUX;
  }

  return null;
};

export const escapeRegex = (regex: string | undefined): string | undefined => {
  if (!regex) {
    return regex;
  }
  return regex.replace(/[-[\]{}()*+?.,\\^$|#]/g, '\\$&');
};

export function isValidEmail(email: string): boolean {
  return !!email.match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
}

export const setCSSVariable = (variableName: string, value: string) => {
  document.documentElement.style.setProperty(`--${variableName}`, value);
};

export const useGetConditionalProjectId = () => {
  const { projectId: projectIdFromParams } = useParams();
  return projectIdFromParams || GET_CURRENT_PROJECT_ID();
};

const getNewTabURL = (subUrl: string) => {
  return `${window.location.protocol}//${window.location.host}${subUrl}`;
};

export const openURLInNewTab = (subUrl: string) => {
  window.open(getNewTabURL(subUrl), '_blank');
};

export const openProjectInNewTab = (projectId: string) => {
  const url = `/${PROJECT_NAVIGATION_PATH}/${projectId}/${PROJECT_NAVIGATION_CANVAS}`;
  openURLInNewTab(url);
};

export const isCommandCtrlClick = (event: React.MouseEvent | KeyboardEvent): boolean => {
  return event.metaKey || event.ctrlKey;
};

export const capitalizeFirstLetter = (word: string) => {
  return word.charAt(0).toUpperCase() + word.slice(1);
};

export const isBlankHTML = (text: string) => {
  const blocksFromHtml = htmlToDraft(text);
  const { contentBlocks, entityMap } = blocksFromHtml;
  const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
  return contentState.getPlainText()?.trim()?.length === 0 || !contentState.hasText();
};

export const getZoomLevelOnContainer = (selector: string) => {
  try {
    const containerElement = document.querySelector(selector);
    if (!containerElement) return null;
    const computedStyle = window.getComputedStyle(containerElement);
    const transform = computedStyle.getPropertyValue('transform');
    const matrix = transform.match(/^matrix\((.+)\)$/);
    if (!matrix?.length) return null;
    const matrixValues = matrix?.[1]?.split(', ');
    return parseFloat(matrixValues?.[0]) || null;
  } catch (error) {
    return null;
  }
};

export const splitIntoChunks = (array: any[], chunkSize: number) => {
  const chunkedArray = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    const chunk = array.slice(i, i + chunkSize);
    chunkedArray.push(chunk);
  }
  return chunkedArray;
};

export const isElementIDDisallowed = (value: string): boolean => {
  const planeraMasterScheduleNamespace = new RegExp(/^[Aa]\d*(_T\d+)?$/);
  const planeraWorkingScheduleNamespace = new RegExp(/^[Bb]\d*(_T\d+)?$/);
  const planeraFutureNamespace = new RegExp(/^[Cc]\d*(_T\d+)?$/);
  const matchesSomeNamespace = planeraMasterScheduleNamespace.test(value) || planeraWorkingScheduleNamespace.test(value) || planeraFutureNamespace.test(value);
  return !!value.trim().length && matchesSomeNamespace;
};

export const getGuidsOfActivityAndItsTasks = (element: ScheduleElement) => {
  const guids = [];
  if (element?.guid) {
    guids.push(element.guid);
  }
  if (element?.elementType === NODE_TYPES.ACTIVITY && element?.children) {
    element?.children?.forEach((child) => {
      if (child.guid) {
        guids.push(child.guid);
      }
    });
  }
  return guids;
};

/**
 * This function calculates the scrollHeight of a text by placing it in an HTML element with specific styles.
 * @param inputText
 * @param styles
 */

export const calculateInputScrollHeight = (inputText: string, styles: string) => {
  const tempElement = document.createElement('div');
  tempElement.setAttribute('style', `position: absolute; visibility: hidden; ${styles}`);
  tempElement.textContent = inputText;
  document.body.appendChild(tempElement);
  const scrollHeight = tempElement.scrollHeight;
  document.body.removeChild(tempElement);
  return scrollHeight;
};

const doubleHeightCache = new Map<string, boolean>();

export const doesActivityNameNeedsDoubleHeight = (name: string) => {
  const cached = doubleHeightCache.get(name);
  if (cached !== undefined) return cached;

  // To calculate the scroll height of the content, apply the same styles as those given to the Activity name input field.
  // Include white-space: pre-wrap to properly handle newline characters in multiline text
  const scrollHeight = calculateInputScrollHeight(name, 'width: 214px; height: auto; font-size: 14px; letter-spacing: 0; line-height: 20px; word-break: break-all; font-weight: var(--fontWeightBold); white-space: pre-wrap;');
  const result = !!(scrollHeight && scrollHeight > 20);
  doubleHeightCache.set(name, result);
  return result;
};

// Exported for testing purposes
export const clearDoubleHeightCache = () => {
  doubleHeightCache.clear();
};

export const textLinesCountInAnElement = (domId: string) => {
  const domElement = document.getElementById(domId);
  if (!domElement) return 0;
  const divHeight = domElement.offsetHeight;
  const computedStyle = window.getComputedStyle(domElement);
  const lineHeight = parseInt(computedStyle.getPropertyValue('line-height'));
  return divHeight / lineHeight;
};

export const sortMapValues = (map: Map<any, any>, sortBy: string): Map<any, any> => {
  try {
    const sortedEntries = Array.from(map?.entries())?.sort((a, b) => a?.[1]?.[sortBy]?.localeCompare(b?.[1]?.[sortBy]));
    return new Map(sortedEntries);
  } catch (error) {
    return map;
  }
};

export const printLag = (lagValue: number) => {
  if (lagValue > 0) return `+${lagValue}`;
  if (lagValue < 0) return `${lagValue}`;
  return 0;
};

export const isNodeIntersectingWithOtherNodes = (nodeToCheck: CanvasNode, allNodes: CanvasNode[]): CanvasNode => {
  if (!allNodes?.length || !nodeToCheck) return nodeToCheck;
  const intersections = allNodes.filter((n) => {
    return n.id !== nodeToCheck.id && n.position.x <= nodeToCheck.position.x + 10 && n.position.x >= nodeToCheck.position.x - 10 && n.position.y <= nodeToCheck.position.y + 10 && n.position.y >= nodeToCheck.position.y - 10 && n.width === nodeToCheck.width && n.height === nodeToCheck.height;
  });
  if (!intersections.length) return nodeToCheck;
  const nodeWithUpdatedPosition = {
    ...nodeToCheck,
    position: {
      x: nodeToCheck.position.x + 20,
      y: nodeToCheck.position.y + 20
    }
  };
  return isNodeIntersectingWithOtherNodes(nodeWithUpdatedPosition, allNodes);
};

export const calculateGradeFromDCMAQualityIndex = (qualityIndex: number): string => {
  if (qualityIndex >= 97) return 'A+';
  else if (qualityIndex >= 93) return 'A';
  else if (qualityIndex >= 90) return 'A-';
  else if (qualityIndex >= 87) return 'B+';
  else if (qualityIndex >= 83) return 'B';
  else if (qualityIndex >= 80) return 'B-';
  else if (qualityIndex >= 77) return 'C+';
  else if (qualityIndex >= 73) return 'C';
  else if (qualityIndex >= 70) return 'C-';
  else if (qualityIndex >= 67) return 'D+';
  else if (qualityIndex >= 63) return 'D';
  else if (qualityIndex >= 60) return 'D-';
  else return 'F';
};

export const extractDomainFromEmail = (email: string) => {
  try {
    if (!isValidEmail(email)) return '';
    const parts = email?.split('@');
    return parts?.[parts?.length - 1]?.toLowerCase();
  } catch (error) {
    return '';
  }
};

export const extractExtensionFromFilename = (filename: string) => {
  try {
    if (!filename?.includes('.')) return '';
    const parts = filename?.split('.');
    return parts?.[parts?.length - 1];
  } catch (error) {
    return '';
  }
};

export const getFileNameWithoutExtension = (filename: string) => {
  try {
    return filename.replace(/\.[^/.]+$/, '');
  } catch (error) {
    return filename;
  }
};

export const isValidPositiveInteger = (num: string) => {
  try {
    const number = Number(num);
    return number > 0 && Number.isInteger(number);
  } catch (error) {
    return false;
  }
};

export const isNonPositiveWeeks = (value: unknown): value is number => {
  return typeof value === 'number' && value <= 0;
};

export const moveCursorToEndForDateInput = (htmlInput: HTMLInputElement | null) => {
  try {
    setTimeout(() => {
      if (htmlInput && htmlInput.value && htmlInput.value?.charAt(htmlInput.value.length - 1) === '/') {
        htmlInput.focus();
        htmlInput.setSelectionRange(htmlInput.value.length + 1, htmlInput.value.length + 1);
      }
    }, 10);
  } catch (error) {
    logger.error('Cannot move cursor to end for date input', { error });
  }
};

export function getViewNameFromPath() {
  const { pathname } = window.location;
  const foundTab = Object.values(PROJECT_TABS).find((tab: string) => pathname.includes(tab));
  return foundTab || undefined;
}

function updateClassStyle(className: string, property: string, value: string) {
  if (!className || !property) return false;

  const selector = '.' + className;

  try {
    // Convert to array for better iteration performance
    const sheets = Array.from(document.styleSheets);

    for (const sheet of sheets) {
      try {
        // Single access check - cssRules getter can be expensive
        const rules = sheet.cssRules;
        if (!rules) continue;

        // Use Array.from for consistent iteration
        for (const rule of Array.from(rules)) {
          if (rule instanceof CSSStyleRule && rule.selectorText === selector) {
            rule.style.setProperty(property, value);
            // Continue to update all matching rules across stylesheets
          }
        }
      } catch (error) {
        if (!(error instanceof DOMException && error.name === 'SecurityError')) {
          logger.debug('Cannot inspect stylesheet', { extra: { error, href: sheet.href } });
        }
        continue;
      }
    }
  } catch (error) {
    logger.debug('Cannot update class style', { extra: { className, error, property, value } });
  }
}

export const showCustomErrorOnAmplifyAuth = (error: string) => {
  const errorElement = document.getElementById(CUSTOM_AMPLIFY_ERROR_ID);
  if (errorElement) {
    errorElement.innerHTML = error;
    updateClassStyle('amplify-alert--error', 'display', 'none');
  }
};

export const hideCustomErrorFromAmplifyAuth = () => {
  showCustomErrorOnAmplifyAuth('');
  updateClassStyle('amplify-alert--error', 'display', 'flex');
};

function getPotentialPositions(rectA: DOMRect, clickX: number, clickY: number, margin: number): { x: number; y: number }[] {
  return [
    { x: clickX - rectA.width - margin, y: clickY - rectA.height - margin }, // Top-left
    { x: clickX + margin, y: clickY - rectA.height - margin }, // Top-right
    { x: clickX - rectA.width - margin, y: clickY + margin }, // Bottom-left
    { x: clickX + margin, y: clickY + margin } // Bottom-right
  ];
}

function isOverlapping(rectA: DOMRect, rectB: DOMRect): boolean {
  return !(rectA.right <= rectB.left || rectA.left >= rectB.right || rectA.bottom <= rectB.top || rectA.top >= rectB.bottom);
}

export function findNearestPositionWithoutOverlap(rectA: DOMRect, clickX: number, clickY: number, rects: DOMRect[], margin: number): { x: number; y: number } | null {
  const positions = getPotentialPositions(rectA, clickX, clickY, margin);
  let bestPosition: { x: number; y: number } | null = positions?.[0];

  for (const position of positions) {
    const tempRectA = new DOMRect(position.x, position.y, rectA.width, rectA.height);
    let overlap = false;

    for (const rect of rects) {
      if (isOverlapping(tempRectA, rect)) {
        overlap = true;
        break;
      }
    }

    // Check if the position is within the viewport
    if (!overlap && position.x >= SIDEBAR_WIDTH && position.y >= TOP_HEADER_HEIGHT && position.x + rectA.width <= window.innerWidth && position.y + rectA.height <= window.innerHeight) {
      bestPosition = position;
      break; // We take the first valid position (closest to the click)
    }
  }

  return bestPosition;
}

export const isHTMLElementTextTruncated = (ref: React.RefObject<HTMLDivElement>) => {
  if (!ref?.current) return false;
  return ref.current.scrollWidth > ref.current.clientWidth;
};
export const getCSSVariable = (variableName: string) => {
  try {
    return getComputedStyle(document.documentElement).getPropertyValue(`--${variableName}`).trim();
  } catch (error) {
    logger.error('Failed to get CCS Variable', { error });
    return '';
  }
};

export const resolveTokenColor = ({ fallback, tokenValue }: { fallback: string; tokenValue: string }) => {
  const variableName = tokenValue.match(/^var\(--(.+)\)$/)?.[1];

  if (!variableName) {
    return tokenValue || fallback;
  }

  return getCSSVariable(variableName) || fallback;
};

export const moveElementInArray = <T>(arr: T[], fromIndex: number, toIndex: number): T[] => {
  if (fromIndex < 0 || fromIndex >= arr.length || toIndex < 0 || toIndex >= arr.length) {
    logger.warn('Invalid indices');
    return arr;
  }

  const newArr = [...arr];
  const element = newArr.splice(fromIndex, 1)[0];
  const moveToIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
  newArr.splice(moveToIndex, 0, element);
  return newArr;
};

export const parseStringValue = (val?: string) => {
  return val ?? '';
};
export const parseNumberValue = (val?: number) => {
  return val ?? 0;
};
export const getCriticalPathElementTooltip = (criticalPathLevel?: number) => {
  if (!criticalPathLevel) return '';
  return `This element is a part of ${criticalPathLevel === CRITICAL_PATH_LEVELS.CRITICAL_PATH ? 'Critical Path' : 'Float Path ' + criticalPathLevel}`;
};

/**
 * Removes a custom stylesheet from the document
 * @param {string} id - The id of the style element to be removed
 */
export function removeCustomStylesheet(id: string): void {
  if (!id) return;
  const styleElement = document.getElementById(id);
  if (styleElement) {
    styleElement.remove();
  }
}

/**
 * Adds a custom stylesheet to the document
 * @param {string} cssString - The CSS string to be added as a stylesheet
 * @param {string} id - The id of the style element for future reference (optional)
 * @param {string} label - The is used to identify styles related to a specific label
 */
export function addCustomStylesheet(cssString: string, id?: string, label?: string): void {
  if (id && document.getElementById(id)) {
    removeCustomStylesheet(id);
  }
  const style = document.createElement('style');
  style.type = 'text/css';
  if (id) {
    style.id = id;
  }
  if (label) {
    style.setAttribute('label', label);
  }
  style.textContent = cssString;
  document.head.appendChild(style);
}

export function removeCustomStylesheetsByLabel(label: string): void {
  const styleElement = document.querySelectorAll(`style[label=${label}]`);
  if (styleElement?.length) {
    styleElement.forEach((ele) => ele?.remove());
  }
}
export const getElementLabel = (elementType: ScheduleElementType | undefined) => {
  switch (elementType) {
    case NODE_TYPES.ACTIVITY:
      return 'Activity';
    case NODE_TYPES.TASK:
      return 'Task';
    case NODE_TYPES.GROUP:
      return 'Group';
    case NODE_TYPES.MILESTONE_START:
    case NODE_TYPES.MILESTONE_INTERIM:
    case NODE_TYPES.MILESTONE_END:
      return 'Milestone';
    default:
      return 'Element';
  }
};

export function truncateText(text: string, maxLength: number) {
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
}

/**
 * Adjusts the positions of dragging nodes to prevent overlapping with other nodes.
 * @param {CanvasNode[]} draggingNodes - The nodes currently being dragged.
 * @param {CanvasNode[]} allNodes - All other nodes on the canvas to check for overlaps.
 * @param {CanvasNode[]} [updatedNodes=[]] - The list of nodes with updated positions (used recursively).
 * @returns {CanvasNode[]} - The list of nodes with updated positions where overlaps are resolved.
 */

export function preventNodesOverlap(draggingNodes: CanvasNode[], allNodes: CanvasNode[], updatedNodes: CanvasNode[] = []): CanvasNode[] {
  const MAX_DEPTH = 100; // Maximum number of iterations to resolve overlaps

  // Utility function to check if two nodes overlap
  const isOverlapping = (nodeA: CanvasNode, nodeB: CanvasNode): boolean => {
    return nodeB.id !== nodeA.id && nodeB.position.x <= nodeA.position.x + 10 && nodeB.position.x >= nodeA.position.x - 10 && nodeB.position.y <= nodeA.position.y + 10 && nodeB.position.y >= nodeA.position.y - 10 && nodeB.width === nodeA.width && nodeB.height === nodeA.height;
  };

  // Function to resolve overlapping for a single node
  const resolveOverlap = (node: CanvasNode, nodes: CanvasNode[]): XYPosition => {
    const newPosition = { ...node.position };
    let hasOverlap = true;
    let depth = 0; // Initialize depth counter

    while (hasOverlap && depth < MAX_DEPTH) {
      hasOverlap = false;
      depth += 1; // Increment depth
      for (const otherNode of nodes) {
        if (node.id !== otherNode.id && isOverlapping({ ...node, position: newPosition }, otherNode)) {
          hasOverlap = true;
          newPosition.x += 20;
          newPosition.y += 20;
          break;
        }
      }
    }

    if (depth >= MAX_DEPTH) {
      logger.warn(`Max depth reached while resolving overlap for node with id: ${node.id}`);
    }

    return newPosition;
  };

  for (const draggingNode of draggingNodes) {
    const updatedPosition = resolveOverlap(draggingNode, [...allNodes, ...updatedNodes]);

    // Add the node with updated position to updatedNodes
    updatedNodes.push({ ...draggingNode, position: updatedPosition });
  }

  return updatedNodes;
}

// convert object to map
export function objectToMap(obj: any) {
  try {
    if (!obj) return new Map();
    const map = new Map();
    for (const key in obj) {
      map.set(key, obj[key]);
    }
    return map;
  } catch (error) {
    return new Map();
  }
}

export function adjustFallbackPosition(fallbackPosition: { x: number; y: number }, refRect: DOMRect, targetRect: DOMRect, viewportWidth: number, viewportHeight: number, minX: number, minY: number): { x: number; y: number } {
  let { x, y } = fallbackPosition;

  // Check if fallback is outside viewport and adjust
  if (x < minX) {
    x = minX;
  } else if (x + targetRect.width > viewportWidth) {
    x = viewportWidth - targetRect.width;
  }

  if (y < minY) {
    y = minY;
  } else if (y + targetRect.height > viewportHeight) {
    y = viewportHeight - targetRect.height;
  }

  // Check if fallback overlaps with reference element and adjust
  const overlapsHorizontally = x < refRect.right && x + targetRect.width > refRect.left;
  const overlapsVertically = y < refRect.bottom && y + targetRect.height > refRect.top;

  if (overlapsHorizontally && overlapsVertically) {
    // Try to position away from reference element
    const spaceRight = viewportWidth - refRect.right;
    const spaceLeft = refRect.left;
    const spaceBottom = viewportHeight - refRect.bottom;
    const spaceTop = refRect.top;

    if (spaceRight >= targetRect.width) {
      x = refRect.right;
    } else if (spaceLeft >= targetRect.width) {
      x = refRect.left - targetRect.width;
    } else if (spaceBottom >= targetRect.height) {
      y = refRect.bottom;
    } else if (spaceTop >= targetRect.height) {
      y = refRect.top - targetRect.height;
    } else {
      // If no space available, position at viewport edges
      x = Math.max(minX, Math.min(x, viewportWidth - targetRect.width));
      y = Math.max(minY, Math.min(y, viewportHeight - targetRect.height));
    }
  }

  return { x, y };
}

export function getOptimalPopperPosition(referenceEl: HTMLElement | null, targetEl: HTMLElement | null, preferredPlacements: string[], verticalOffset?: number, fallbackPlacement?: string): { x: number; y: number } {
  try {
    if (!referenceEl || !targetEl) {
      return { x: 0, y: 0 };
    }
    const refRect = referenceEl.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const minX = 0;
    const minY = 0;

    const placementsMap: Record<string, { x: number; y: number }> = {
      'bottom-start': { x: refRect.left, y: refRect.bottom },
      bottom: { x: refRect.left + refRect.width / 2 - targetRect.width / 2, y: refRect.bottom },
      'bottom-end': { x: refRect.right - targetRect.width, y: refRect.bottom },
      'top-start': { x: refRect.left, y: refRect.top - targetRect.height },
      top: { x: refRect.left + refRect.width / 2 - targetRect.width / 2, y: refRect.top - targetRect.height },
      'top-end': { x: refRect.right - targetRect.width, y: refRect.top - targetRect.height },
      'left-start': { x: refRect.left - targetRect.width, y: refRect.top },
      left: { x: refRect.left - targetRect.width, y: refRect.top + refRect.height / 2 - targetRect.height / 2 },
      'left-end': { x: refRect.left - targetRect.width, y: refRect.bottom - targetRect.height },
      'right-start': { x: refRect.right, y: refRect.top },
      right: { x: refRect.right, y: refRect.top + refRect.height / 2 - targetRect.height / 2 },
      'right-end': { x: refRect.right, y: refRect.bottom - targetRect.height }
    };

    for (const placement of preferredPlacements) {
      const { x, y } = placementsMap[placement];
      if (x >= minX && x + targetRect.width <= viewportWidth && y >= minY && y + targetRect.height <= viewportHeight) {
        if (placement?.includes('top') && verticalOffset) {
          return { x, y: y - verticalOffset };
        } else if (placement?.includes('bottom') && verticalOffset) {
          return { x, y: y + verticalOffset };
        }
        return { x, y };
      }
    }

    // Default to the first preferred placement if none fit
    const fallback = placementsMap[fallbackPlacement ?? preferredPlacements[0]];

    return adjustFallbackPosition(fallback, refRect, targetRect, viewportWidth, viewportHeight, minX, minY);
  } catch (error) {
    return { x: 0, y: 0 };
  }
}

const negativeMillion = -1000000;
export const defaultOutOfScreenCoordinates = { posX: negativeMillion, posY: negativeMillion };

export const setContextMenuXY = ({ eventObject, targetElementId, leftNavigationExpanded }: { eventObject: { event: MouseEvent | null; fromKebabMenu: boolean }; targetElementId: string; leftNavigationExpanded?: boolean }) => {
  if (!eventObject.event) {
    return;
  }

  const menu = document.getElementById(targetElementId);

  if (!menu) return;
  const leftNavWidth = leftNavigationExpanded ? 220 : 74;
  const kebabMenuWidth = eventObject.fromKebabMenu ? 30 : 0;
  const clickX = eventObject.event.pageX;
  const clickY = eventObject.event.pageY;
  const menuWidth = menu.offsetWidth;
  const menuHeight = menu.offsetHeight;
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;
  let posX = clickX;
  let posY = clickY;
  const spaceBelow = windowHeight - clickY; // Space available below cursor
  const spaceAbove = clickY; // Space available above cursor
  const posXAdjustment = leftNavWidth - kebabMenuWidth;

  if (spaceBelow >= menuHeight) {
    // Enough space below, show below cursor
    posY = clickY;
  } else if (spaceAbove >= menuHeight) {
    // Enough space above, show above cursor
    posY = clickY - menuHeight;
  } else {
    // Not enough space above or below, center around click position
    posY = clickY - menuHeight / 2;
  }

  // Ensure menu stays inside the right boundary
  if (clickX + menuWidth > windowWidth) {
    posX = windowWidth - menuWidth; // Move it inside the right boundary
  }

  return { posX: posX - posXAdjustment, posY };
};

export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    if (!(file instanceof File)) {
      reject(new Error('Invalid file object'));
      return;
    }
    const img = new Image();
    const objectURL = URL.createObjectURL(file);

    img.src = objectURL;

    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(objectURL); // Cleanup
    };

    img.onerror = (error) => {
      reject(unknownToError(error, 'getImageDimensions error'));
    };
  });
};

export const truncateLabel = (label: string, maxLength: number = 20) => {
  return label.length > maxLength ? label.slice(0, maxLength) + '...' : label;
};

export const convertHTMLToPlainText = (html: string) => {
  try {
    const replaced = html.replace(/<br\b[^>]*\/?>/gi, ' ').replace(/<\/(p|div|li|h[1-6])>/gi, ' ');
    const parser = new DOMParser();
    const doc = parser.parseFromString(replaced, 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  } catch (error) {
    return html;
  }
};

type ElementWithType = {
  elementType: ScheduleElementType;
};

export const getLabelFromSelectedElements = (selectedElements: ElementWithType[]) => {
  const elementType = getFirstValidElementType(selectedElements);
  const isPlural = selectedElements.length > 1;

  switch (elementType) {
    case NODE_TYPES.ACTIVITY:
      return isPlural ? 'Activities' : 'Activity';
    case NODE_TYPES.TASK:
      return isPlural ? 'Tasks' : 'Task';
    case NODE_TYPES.GROUP:
      return isPlural ? 'Groups' : 'Group';
    case NODE_TYPES.MILESTONE_START:
    case NODE_TYPES.MILESTONE_INTERIM:
    case NODE_TYPES.MILESTONE_END:
      return isPlural ? 'Milestones' : 'Milestone';
    default:
      return isPlural ? 'Elements' : 'Element';
  }
};
export const safeNumber = (value: number | null | undefined): number => (Number.isFinite(value) ? (value as number) : 0);
